import { useState } from 'react'
import { CloseIcon } from '@/components/icons/baba-icons'
import { trackAppStoreClick } from '@/lib/analytics'
import { getStoreUrl, detectPlatform, type StorePlatform } from '@/lib/storeLinks'
import { FREE_CHARS_PER_MONTH } from '@/lib/baba-limits'
import { useSignIn } from '../SignInModal'

const DISMISS_KEY = 'baba.app.phone_nudge_dismissed'

function wasDismissed(): boolean {
  try {
    return localStorage.getItem(DISMISS_KEY) === '1'
  } catch {
    return false
  }
}

function StorePill({ platform, placement }: { platform: StorePlatform; placement: string }) {
  const isIos = platform === 'ios'
  return (
    <a
      href={getStoreUrl(platform, placement)}
      target="_blank"
      rel="noopener noreferrer"
      onClick={() => trackAppStoreClick(platform, placement)}
      className="inline-flex items-center gap-1.5 h-8 px-3.5 rounded-md border border-app bg-surface-2 hover:bg-brand-soft hover:border-[var(--primary)]/40 text-muted hover:text-app transition-all text-[12px] font-semibold"
      dir="ltr"
    >
      {isIos ? (
        <svg viewBox="0 0 24 24" className="h-3.5 w-3.5 shrink-0" fill="currentColor" role="img" aria-label="Apple">
          <path d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701" />
        </svg>
      ) : (
        <svg viewBox="0 0 24 24" className="h-3.5 w-3.5 shrink-0" fill="currentColor" role="img" aria-label="Google Play">
          <path d="M22.018 13.298l-3.919 2.218-3.515-3.493 3.543-3.521 3.891 2.202a1.49 1.49 0 0 1 0 2.594zM1.337.924a1.486 1.486 0 0 0-.112.568v21.017c0 .217.045.419.124.6l11.155-11.087L1.337.924zm12.207 10.065l3.258-3.238L3.45.195a1.466 1.466 0 0 0-.946-.179l11.04 10.973zm0 2.067l-11 10.933c.298.036.612-.016.906-.183l13.324-7.54-3.23-3.21z" />
        </svg>
      )}
      {isIos ? 'App Store' : 'Google Play'}
    </a>
  )
}

/**
 * Subtle, dismissible affordance shown beneath a finished translation — the
 * moment of value. Guests get a free sign-in pitch (they don't have an
 * account to install the app with); signed-in users get the phone nudge,
 * device-aware: iOS → App Store, Android → Play, desktop → both. Dismissal
 * persists so we only ask once.
 */
export function GetOnPhoneNudge({ isAuthed }: { isAuthed: boolean }) {
  const [hidden, setHidden] = useState(() => wasDismissed())
  const { open: openSignIn } = useSignIn()
  if (hidden) return null

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, '1')
    } catch {}
    setHidden(true)
  }

  if (!isAuthed) {
    return (
      <div className="flex items-center gap-2 flex-wrap text-[12.5px] text-subtle">
        <button
          type="button"
          onClick={() => openSignIn('upgrade-free')}
          className="text-left font-medium text-brand hover:underline"
        >
          Sign in free to save this translation and get {FREE_CHARS_PER_MONTH.toLocaleString()} characters a month
        </button>
        <button
          type="button"
          aria-label="Dismiss"
          onClick={dismiss}
          className="ml-auto h-7 w-7 flex items-center justify-center rounded-md text-subtle hover:text-app hover:bg-[var(--hover)] transition-colors"
        >
          <CloseIcon size={10} />
        </button>
      </div>
    )
  }

  const device = detectPlatform()
  const stores: StorePlatform[] =
    device === 'ios' ? ['ios'] : device === 'android' ? ['android'] : ['ios', 'android']

  return (
    <div className="flex items-center gap-2 flex-wrap text-[12.5px] text-subtle">
      <span className="font-medium">Get baba on your phone</span>
      {stores.map((p) => (
        <StorePill key={p} platform={p} placement="translator" />
      ))}
      <button
        type="button"
        aria-label="Dismiss"
        onClick={dismiss}
        className="ml-auto h-7 w-7 flex items-center justify-center rounded-md text-subtle hover:text-app hover:bg-[var(--hover)] transition-colors"
      >
        <CloseIcon size={10} />
      </button>
    </div>
  )
}
